import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@noor/ui';

/**
 * The destructive-action confirmation, reproducing the old panel's
 * `ConfirmDialog`: a 24px-radius centred sheet with 40px padding, a warning
 * mark, the question, a grey description, and two 56px-tall buttons at a
 * 196px minimum width.
 *
 * Built on the design system's Radix `Dialog` rather than a bare overlay so
 * focus trapping, Escape-to-close, scroll locking and the `alertdialog`
 * semantics come for free — those are behaviors, not visuals, and the
 * reproduction has no reason to re-implement them worse.
 */
interface LegacyConfirmDialogProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel: string;
  /** Cancel-button label; defaults to `profile.cancel`, the original's text. */
  cancelLabel?: string;
  /**
   * Confirm-button color. The old `ConfirmDialog` took arbitrary action
   * buttons: destructive screens passed a red one, while others (staff delete,
   * integration allow) used the default contained button — brand orange.
   * Defaults to the red the existing callers expect.
   */
  confirmVariant?: 'danger' | 'brand';
  /** Disables both buttons and dims the confirm while the mutation runs. */
  isPending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function LegacyConfirmDialog(props: LegacyConfirmDialogProps) {
  /*
   * Nothing renders while closed — not even the Radix `Dialog` wrapper.
   *
   * Radix keeps a closing dialog mounted until its exit animation reports
   * `animationend`. Inside `.legacy-root` that never arrives (observed live:
   * `data-state` flips to `closed`, no animation remains pending, and the
   * sheet stays visible at full opacity with its overlay still blocking the
   * page). Unmounting at the source removes the exit path entirely — the same
   * fix `legacy-order-drawer` needed.
   *
   * The cost is no close animation, which is invisible next to a confirmation
   * that would not go away.
   */
  if (!props.open) return null;
  return <ConfirmDialogBody {...props} />;
}

function ConfirmDialogBody({
  title,
  description,
  confirmLabel,
  cancelLabel,
  confirmVariant = 'danger',
  isPending = false,
  onConfirm,
  onCancel,
}: LegacyConfirmDialogProps) {
  const { t } = useTranslation('legacy');

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        // Escape / overlay click cancels — but never mid-flight, or the user
        // loses sight of an action that is still going to land.
        if (!next && !isPending) onCancel();
      }}
    >
      <DialogContent
        data-legacy
        // White sheet, matching the old `StyledDialog`. Its dismiss button is a
        // grey pill, which vanishes on `.legacy-root`'s page grey.
        className="legacy-root legacy-sheet max-w-[676px] gap-0 rounded-[24px] border-0 p-10 text-center sm:max-w-[676px]"
      >
        <div className="flex flex-col items-center">
          {/* Warning mark — the original's `BigWarningIcon`. */}
          <span
            aria-hidden="true"
            className="mb-4 flex size-20 items-center justify-center rounded-full bg-[var(--lg-red-600)]/10 text-[40px] font-bold text-[var(--lg-red-600)]"
          >
            !
          </span>

          <DialogTitle className="text-[24px] font-bold text-[var(--lg-fg)]">
            {title}
          </DialogTitle>

          {description ? (
            <DialogDescription className="mt-4 text-[14px] leading-4 font-medium text-[var(--lg-grey-400)]">
              {description}
            </DialogDescription>
          ) : null}

          <div className="mt-8 flex w-full flex-col-reverse justify-center gap-4 sm:flex-row">
            <button
              type="button"
              onClick={onCancel}
              disabled={isPending}
              className="h-14 min-w-[196px] cursor-pointer rounded-[96px] bg-[var(--lg-grey-100)] text-[16px] font-semibold text-[var(--lg-fg)] transition-opacity hover:opacity-80 disabled:opacity-50"
            >
              {cancelLabel ?? t('profile.cancel')}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isPending}
              className={`h-14 min-w-[196px] cursor-pointer rounded-[96px] ${
                confirmVariant === 'brand'
                  ? 'bg-[var(--lg-orange-500)]'
                  : 'bg-[var(--lg-red-600)]'
              } text-[16px] font-semibold text-[var(--lg-surface)] transition-opacity hover:opacity-90 disabled:opacity-50`}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
